<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div
        v-if="isSuccess"
        :class="{ 'full-height': !items.length }"
      >
        <div v-if="items.length" class="table">
          <v-layout class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong>&nbsp;{{ $t('Common.of') }}&nbsp;<strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.ridesMade`) }}</span>

            <v-spacer />

            <a
              v-if="claims.report['ROLE_GET_REPORT_CHART']"
              :title="$t(`${namespace}.showPerformanceChart`)"
              class="accent--text"
              @click="handlePerformanceChart"
            >{{ $t(`${namespace}.showPerformanceChart`) }}</a>
            <v-icon
              v-if="claims.report['ROLE_GET_REPORT_CHART']"
              size="18"
              class="ml-1"
            >
              insert_chart
            </v-icon>
          </v-layout>

          <v-layout class="table__header" align-center>
            <v-flex style="min-width: 130px;">ID</v-flex>
            <v-flex xs1>{{ $t('Common.date') | capitalizeFirstLetter}}</v-flex>
            <v-flex xs3>{{ $t(`${namespace}.employeeName`) }}</v-flex>
            <v-flex xs2>{{ $t('Common.costCenter') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs2>{{ $t('Common.category') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.distance`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.duration`) }}</v-flex>
            <v-flex xs1>{{ $t(`Common.value`) }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex style="min-width: 130px;">
                        <span :title="item.apiInfo.apiTaxiJobId" class="text--ellipsis" contenteditable>{{ item.apiInfo.apiTaxiJobId }}</span>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="`${moment(item.start.date).format('DD/MM/YYYY')} início às ${moment(item.start.date).format('HH:mm')}`">
                          {{ item.start.date | formatDate('DD/MM/YYYY') }}
                          <br>
                          <span
                            class="gray-6--text caption"
                          >Início às {{ moment(item.start.date).format('HH:mm') }}</span>
                        </span>
                      </v-flex>

                      <v-flex xs3>
                        <span :title="item.employee.name" class="body-1 text--ellipsis">
                          {{ item.employee.name }}
                        </span>
                        <span :title="item.employee.email" class="gray-6--text caption text--ellipsis">
                          {{ item.employee.email }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span
                          v-if="item.costCenter.name.length > 3"
                          :title="item.costCenter.name"
                          class="text--ellipsis"
                        >
                          {{ item.costCenter.name }}
                        </span>
                        <span
                          v-else
                          :title="item.costCenter.name"
                          class="text--ellipsis"
                        >
                          {{ item.costCenter.name }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span :title="getRideCategory(item.taxiCategoryName).text">
                          {{ getRideCategory(item.taxiCategoryName).text }}
                        </span>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.odometer | metresToKm">
                          {{ item.odometer | metresToKm }}
                        </span>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.start.date | durationCalc(item.end.date)">
                          {{ item.start.date | durationCalc(item.end.date) }}
                        </span>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.fare | currencyBRL">
                          {{ item.fare | currencyBRL }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="employee"
          :text="$t(`${namespace}.rideNotFound`)"
          style="padding-bottom: 120px"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber, getRideCategory } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    clearSelectedItem: {
      type: Boolean
    }
  },
  components: {
    EmptyState
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.ReportsPage',
      selectedItem: null
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    getRideCategory (category = '') {
      return getRideCategory(this, category)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    handlePerformanceChart () {
      this.$emit('openPerformanceChartDialog')
    },

    activateLine (item) {
      return item.taxiJobId === this.selectedItem
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.taxiJobId
        this.$emit('openDrawer', item)
      }
    }
  },
  watch: {
    clearSelectedItem () {
      this.selectedItem = null
    }
  }
}
</script>
